/**
 * ListButtonsPanel — zakładka „Przyciski" w Kreatorze Listy.
 *
 * Dwie sekcje:
 *  1. Etykiety standardowych przycisków — własne nazwy dla stałego zestawu akcji
 *     nad listą (create / refresh / export / import / operations). Pusta etykieta
 *     = runtime użyje domyślnej. Zapis do `list_config.buttonsConfig`.
 *  2. Własne przyciski nawigacyjne (builder) — user dodaje własne przyciski z
 *     etykietą, ikoną, wariantem koloru, pozycją (pasek/akcja wiersza/akcja
 *     masowa) i akcją nawigacyjną (przejdź / otwórz URL / odśwież). Zapis do
 *     `list_config.customActionButtons`.
 *
 * Przyciski-akcji (mail / webhook / zadanie / zmiana pola) = automatyzacja —
 * dojdą w późniejszym etapie razem z silnikiem akcji (moduł Procesy/akceptacje).
 *
 * Zapis przez `useTableLayout(slug)` (PUT — zachowuje kolumny/sort/filtry/
 * quickFilters). Runtime wpięty na stronie rekordów modułu (ModuleRecordsPage).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ButtonConfig, CustomActionButton, CustomButtonAction } from '@saas/shared';
import { ArrowDown, ArrowUp, Plus, Save, Trash2, X } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { IconPicker } from '@/components/modules/IconPicker';
import { useToast } from '@/contexts/ToastContext';

import { safeExternalUrl } from './customButtonActions';
import { useTableLayout } from './useTableLayout';

/** Stały zestaw akcji nad listą — kolejność = kolejność wyświetlania w panelu. */
const STANDARD_ACTIONS = ['create', 'refresh', 'export', 'import', 'operations'] as const;
type StandardAction = (typeof STANDARD_ACTIONS)[number];

const VARIANTS = ['primary', 'secondary', 'outline', 'ghost', 'danger'] as const;
const POSITIONS = ['toolbar', 'row_actions', 'bulk_actions'] as const;
/** Typy akcji do wyboru dla przycisku. „Odśwież listę" NIE jest proponowany (szkolenie 14.10:
 *  lista ma wbudowany przycisk odświeżania, więc własny jest zbędny) — zostaje widoczny tylko
 *  przy przycisku, który już go ma (wsteczna zgodność zapisanych układów). */
const ACTION_TYPES = ['navigate', 'open_url'] as const;
function actionTypeOptions(current: CustomButtonAction['type']): CustomButtonAction['type'][] {
  return current === 'refresh' ? [...ACTION_TYPES, 'refresh'] : [...ACTION_TYPES];
}

/** Błąd ścieżki / adresu przycisku: pusty, niebezpieczny schemat, `{id}` na pasku (brak rekordu). */
type TargetError = 'required' | 'unsafe' | 'toolbarVars';

function targetError(btn: CustomActionButton): TargetError | null {
  const a = btn.action;
  if (a.type === 'refresh') return null;
  const raw = (a.type === 'navigate' ? a.to : a.url).trim();
  if (!raw) return 'required';
  const hasVars = /\{ids?\}/.test(raw);
  if (hasVars && btn.position === 'toolbar') return 'toolbarVars';
  // Zmienne podstawiane w runtime — do sprawdzenia schematu wstawiamy neutralną wartość.
  if (a.type === 'open_url' && safeExternalUrl(raw.replace(/\{ids?\}/g, 'x')) === null) {
    return 'unsafe';
  }
  return null;
}

const TARGET_ERROR_KEYS: Record<TargetError, string> = {
  required: 'tenant.listBuilder.buttons.custom.targetRequired',
  unsafe: 'tenant.listBuilder.buttons.custom.unsafeUrl',
  toolbarVars: 'tenant.listBuilder.buttons.custom.toolbarVars',
};

/** Domyślny kształt akcji dla wybranego typu (reset parametrów przy zmianie). */
function defaultAction(type: CustomButtonAction['type']): CustomButtonAction {
  if (type === 'navigate') return { type: 'navigate', to: '' };
  if (type === 'open_url') return { type: 'open_url', url: '', newTab: true };
  return { type: 'refresh' };
}

export function ListButtonsPanel({ slug }: { slug: string }): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);

  // Mapa actionId → własna etykieta (pusta = użyj domyślnej).
  const [labels, setLabels] = useState<Record<string, string> | null>(null);
  // Własne przyciski nawigacyjne (builder).
  const [customButtons, setCustomButtons] = useState<CustomActionButton[] | null>(null);
  // Przycisk oczekujący na potwierdzenie usunięcia.
  const [pendingDelete, setPendingDelete] = useState<CustomActionButton | null>(null);
  // Czy próbowano zapisać — odblokowuje pokazywanie błędów walidacji inline.
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (layout?.layoutConfig) {
      const map: Record<string, string> = {};
      for (const b of layout.layoutConfig.buttonsConfig ?? []) {
        if (b.customLabel) map[b.actionId] = b.customLabel;
      }
      setLabels(map);
      setCustomButtons(
        [...(layout.layoutConfig.customActionButtons ?? [])].sort((a, b) => a.order - b.order),
      );
    }
  }, [layout]);

  if (isLoading) return <p className="text-sm text-text-tertiary">…</p>;
  if (!layout || !labels || !customButtons) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  const setLabel = (actionId: StandardAction, value: string) =>
    setLabels((m) => ({ ...m, [actionId]: value }));

  const updateButton = (id: string, patch: Partial<CustomActionButton>) =>
    setCustomButtons((list) => (list ?? []).map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const addButton = () =>
    setCustomButtons((list) => [
      ...(list ?? []),
      {
        id: crypto.randomUUID(),
        label: '',
        variant: 'secondary',
        position: 'toolbar',
        action: { type: 'navigate', to: '' },
        order: (list ?? []).length,
      },
    ]);

  const removeButton = (id: string) =>
    setCustomButtons((list) =>
      (list ?? []).filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })),
    );

  // Zamiana pozycji z sąsiadem (zmiana kolejności = order).
  const moveButton = (id: string, dir: -1 | 1) =>
    setCustomButtons((list) => {
      const arr = [...(list ?? [])];
      const i = arr.findIndex((b) => b.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
      return arr.map((b, idx) => ({ ...b, order: idx }));
    });

  const handleSave = async () => {
    // Walidacja: każdy własny przycisk musi mieć etykietę. Pusta = błąd
    // (nie cichoporzucamy — user musi wpisać nazwę albo usunąć wiersz).
    const hasEmptyLabel = customButtons.some((b) => b.label.trim().length === 0);
    if (hasEmptyLabel) {
      setShowErrors(true);
      toast.show({
        variant: 'error',
        title: t('tenant.listBuilder.buttons.custom.labelRequiredToast'),
      });
      return;
    }
    // Ścieżka / adres to warunek działania przycisku — pusty, niebezpieczny albo z `{id}` na
    // pasku (nie ma czym podstawić) = przycisk bez efektu.
    if (customButtons.some((b) => targetError(b) !== null)) {
      setShowErrors(true);
      toast.show({
        variant: 'error',
        title: t('tenant.listBuilder.buttons.custom.targetRequiredToast'),
      });
      return;
    }

    // Etykiety standardowych — persystujemy tylko akcje z niepustą etykietą.
    const buttonsConfig: ButtonConfig[] = STANDARD_ACTIONS.map((a) => ({
      actionId: a,
      label: (labels[a] ?? '').trim(),
    }))
      .filter((b) => b.label.length > 0)
      .map((b) => ({ actionId: b.actionId, customLabel: b.label }));

    // Własne przyciski — etykiety przycięte, przenumerowanie order.
    const cleaned: CustomActionButton[] = customButtons.map((b, i) => ({
      ...b,
      label: b.label.trim(),
      order: i,
    }));

    try {
      await saveLayout(
        { ...layout.layoutConfig, buttonsConfig, customActionButtons: cleaned },
        { throwOnForbidden: true },
      );
    } catch {
      toast.show({ variant: 'error', title: t('tenant.listBuilder.buttons.saveError') });
      return;
    }
    setShowErrors(false);
    toast.show({ variant: 'success', title: t('tenant.listBuilder.buttons.saved') });
  };

  return (
    <div className="space-y-6" data-testid="list-buttons-panel">
      {/* Sekcja 1 — etykiety standardowych przycisków */}
      <div className="space-y-3">
        <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.buttons.intro')}</p>
        <div className="space-y-2">
          {STANDARD_ACTIONS.map((action) => (
            <div
              key={action}
              className="flex items-center gap-3 rounded-lg border border-border-secondary bg-bg-elevated px-3 py-2"
              data-testid={`list-buttons-row-${action}`}
            >
              <span className="min-w-[160px] text-sm font-medium text-text-primary">
                {t(`tenant.listBuilder.buttons.actions.${action}`)}
              </span>
              <input
                type="text"
                value={labels[action] ?? ''}
                onChange={(e) => setLabel(action, e.target.value)}
                placeholder={t('tenant.listBuilder.buttons.labelPlaceholder')}
                className="flex-1 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
                data-testid={`list-buttons-input-${action}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Sekcja 2 — własne przyciski nawigacyjne (builder) */}
      <div className="space-y-3 border-t border-border-secondary pt-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            {t('tenant.listBuilder.buttons.custom.title')}
          </h3>
          <p className="text-xs text-text-muted">{t('tenant.listBuilder.buttons.custom.intro')}</p>
        </div>

        <div className="space-y-3">
          {customButtons.map((btn, idx) => {
            const btnTargetError = showErrors ? targetError(btn) : null;
            return (
              <div
                key={btn.id}
                className="space-y-2 rounded-lg border border-border-secondary bg-bg-elevated p-3"
                data-testid={`list-buttons-custom-row-${idx}`}
              >
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex-1 min-w-[140px] text-xs text-text-muted">
                    {t('tenant.listBuilder.buttons.custom.label')}
                    <span className="text-error-500"> *</span>
                    <input
                      type="text"
                      value={btn.label}
                      onChange={(e) => updateButton(btn.id, { label: e.target.value })}
                      placeholder={t('tenant.listBuilder.buttons.custom.labelPlaceholder')}
                      maxLength={100}
                      aria-invalid={showErrors && btn.label.trim().length === 0}
                      className={`mt-0.5 w-full rounded border bg-bg-elevated px-2 py-1 text-sm text-text-primary ${
                        showErrors && btn.label.trim().length === 0
                          ? 'border-error-500 focus:border-error-500 focus:ring-1 focus:ring-error-500'
                          : 'border-border-secondary'
                      }`}
                      data-testid={`list-buttons-custom-label-${idx}`}
                    />
                    {showErrors && btn.label.trim().length === 0 && (
                      <span
                        className="mt-0.5 block text-error-500"
                        data-testid={`list-buttons-custom-label-error-${idx}`}
                      >
                        {t('tenant.listBuilder.buttons.custom.labelRequired')}
                      </span>
                    )}
                  </label>
                  <div className="text-xs text-text-muted">
                    <span className="block">{t('tenant.listBuilder.buttons.custom.icon')}</span>
                    <div className="mt-0.5 flex items-center gap-1">
                      <IconPicker
                        value={btn.icon ?? ''}
                        onChange={(name) => updateButton(btn.id, { icon: name })}
                        ariaLabel={t('tenant.listBuilder.buttons.custom.icon')}
                      />
                      {btn.icon && (
                        <button
                          type="button"
                          onClick={() => updateButton(btn.id, { icon: undefined })}
                          className="rounded p-1 text-text-tertiary hover:text-text-primary"
                          aria-label={t('tenant.listBuilder.buttons.custom.iconClear')}
                          title={t('tenant.listBuilder.buttons.custom.iconClear')}
                          data-testid={`list-buttons-custom-icon-clear-${idx}`}
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-xs text-text-muted">
                    {t('tenant.listBuilder.buttons.custom.variant')}
                    <Dropdown
                      options={VARIANTS.map((v) => ({
                        value: v,
                        label: t(`tenant.listBuilder.buttons.custom.variants.${v}`),
                      }))}
                      value={btn.variant}
                      onChange={(val) =>
                        updateButton(btn.id, {
                          variant: val as CustomActionButton['variant'],
                        })
                      }
                      aria-label={t('tenant.listBuilder.buttons.custom.variant')}
                      className="mt-0.5 w-auto px-2 py-1"
                      data-testid={`list-buttons-custom-variant-${idx}`}
                    />
                  </label>
                  <label className="text-xs text-text-muted">
                    {t('tenant.listBuilder.buttons.custom.position')}
                    <Dropdown
                      options={POSITIONS.map((p) => ({
                        value: p,
                        label: t(`tenant.listBuilder.buttons.custom.positions.${p}`),
                      }))}
                      value={btn.position}
                      onChange={(val) =>
                        updateButton(btn.id, {
                          position: val as CustomActionButton['position'],
                        })
                      }
                      aria-label={t('tenant.listBuilder.buttons.custom.position')}
                      className="mt-0.5 w-auto px-2 py-1"
                      data-testid={`list-buttons-custom-position-${idx}`}
                    />
                  </label>
                  <label className="text-xs text-text-muted">
                    {t('tenant.listBuilder.buttons.custom.actionType')}
                    <Dropdown
                      options={actionTypeOptions(btn.action.type).map((a) => ({
                        value: a,
                        label: t(`tenant.listBuilder.buttons.custom.actions.${a}`),
                      }))}
                      value={btn.action.type}
                      onChange={(val) =>
                        updateButton(btn.id, {
                          action: defaultAction(val as CustomButtonAction['type']),
                        })
                      }
                      aria-label={t('tenant.listBuilder.buttons.custom.actionType')}
                      className="mt-0.5 w-auto px-2 py-1"
                      data-testid={`list-buttons-custom-action-${idx}`}
                    />
                  </label>
                </div>

                {/* Parametr akcji zależny od typu */}
                {btn.action.type === 'navigate' && (
                  <input
                    type="text"
                    value={btn.action.to}
                    onChange={(e) =>
                      updateButton(btn.id, { action: { type: 'navigate', to: e.target.value } })
                    }
                    placeholder={t('tenant.listBuilder.buttons.custom.navigatePlaceholder')}
                    aria-invalid={btnTargetError ? true : undefined}
                    className={`w-full rounded border bg-bg-elevated px-2 py-1 text-sm text-text-primary ${
                      btnTargetError
                        ? 'border-error-500 focus:border-error-500 focus:ring-1 focus:ring-error-500'
                        : 'border-border-secondary'
                    }`}
                    data-testid={`list-buttons-custom-navigate-${idx}`}
                  />
                )}
                {btn.action.type === 'open_url' && (
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={btn.action.url}
                      onChange={(e) =>
                        updateButton(btn.id, {
                          action: {
                            type: 'open_url',
                            url: e.target.value,
                            newTab: btn.action.type === 'open_url' ? btn.action.newTab : true,
                          },
                        })
                      }
                      placeholder="https://…"
                      aria-invalid={btnTargetError ? true : undefined}
                      className={`flex-1 rounded border bg-bg-elevated px-2 py-1 text-sm text-text-primary ${
                        btnTargetError
                          ? 'border-error-500 focus:border-error-500 focus:ring-1 focus:ring-error-500'
                          : 'border-border-secondary'
                      }`}
                      data-testid={`list-buttons-custom-url-${idx}`}
                    />
                    <label className="flex items-center gap-1.5 text-xs text-text-muted">
                      <input
                        type="checkbox"
                        checked={btn.action.newTab ?? false}
                        onChange={(e) =>
                          updateButton(btn.id, {
                            action: {
                              type: 'open_url',
                              url: btn.action.type === 'open_url' ? btn.action.url : '',
                              newTab: e.target.checked,
                            },
                          })
                        }
                        data-testid={`list-buttons-custom-newtab-${idx}`}
                      />
                      {t('tenant.listBuilder.buttons.custom.newTab')}
                    </label>
                  </div>
                )}
                {btnTargetError && (
                  <span
                    className="block text-xs text-error-500"
                    data-testid={`list-buttons-custom-target-error-${idx}`}
                  >
                    {t(TARGET_ERROR_KEYS[btnTargetError])}
                  </span>
                )}

                {btn.position !== 'toolbar' && btn.action.type !== 'refresh' && (
                  <p
                    className="text-xs text-text-muted"
                    data-testid={`list-buttons-custom-vars-hint-${idx}`}
                  >
                    {t(
                      btn.position === 'row_actions'
                        ? 'tenant.listBuilder.buttons.custom.rowVarsHint'
                        : 'tenant.listBuilder.buttons.custom.bulkVarsHint',
                    )}
                  </p>
                )}

                {/* Sterowanie wierszem: kolejność + usuń */}
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => moveButton(btn.id, -1)}
                    disabled={idx === 0}
                    className="rounded p-1 text-text-tertiary hover:text-text-primary disabled:opacity-30"
                    aria-label={t('common.moveUp')}
                    data-testid={`list-buttons-custom-up-${idx}`}
                  >
                    <ArrowUp size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveButton(btn.id, 1)}
                    disabled={idx === customButtons.length - 1}
                    className="rounded p-1 text-text-tertiary hover:text-text-primary disabled:opacity-30"
                    aria-label={t('common.moveDown')}
                    data-testid={`list-buttons-custom-down-${idx}`}
                  >
                    <ArrowDown size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(btn)}
                    className="rounded p-1 text-error-500 hover:text-error-600"
                    aria-label={t('common.delete')}
                    data-testid={`list-buttons-custom-delete-${idx}`}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={addButton}
          className="inline-flex items-center gap-2 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
          data-testid="list-buttons-custom-add"
        >
          <Plus size={16} aria-hidden="true" />
          {t('tenant.listBuilder.buttons.custom.add')}
        </button>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid="list-buttons-save"
        >
          <Save size={16} aria-hidden="true" />
          {isSaving
            ? t('tenant.listBuilder.buttons.saving')
            : t('tenant.listBuilder.buttons.saveBtn')}
        </button>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.listBuilder.buttons.custom.deleteTitle')}
        message={
          pendingDelete
            ? t('tenant.listBuilder.buttons.custom.deleteMessage', {
                name: pendingDelete.label || t('tenant.listBuilder.buttons.custom.unnamed'),
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) removeButton(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
